import React, { useState, useCallback } from 'react';
import { Tooltip } from '../Tooltip';

/* ===============================
   TYPES
   =============================== */

type SegmentedButtonSize = 'sm' | 'md' | 'lg';
type SegmentedButtonVariant = 'filled' | 'outlined' | 'ghost';

export interface SegmentOption {
  value: string;
  label: string;
  icon?: React.ReactNode;
  disabled?: boolean;
  tooltip?: string;
}

export interface SegmentedButtonProps {
  /** Array of options to display */
  options: SegmentOption[];
  
  /** Currently selected value (for controlled component) */
  value?: string;
  
  /** Default selected value (for uncontrolled component) */
  defaultValue?: string;
  
  /** Callback fired when selection changes */
  onChange?: (value: string) => void;
  
  /** Size variant */
  size?: SegmentedButtonSize;
  
  /** Visual variant */
  variant?: SegmentedButtonVariant;
  
  /** Make the component full width */
  fullWidth?: boolean;
  
  /** Disable all segments */
  disabled?: boolean;
  
  /** Additional CSS classes */
  className?: string;
  
  /** Accessible label for the group */
  'aria-label'?: string;
}

/* ===============================
   BASE STYLES
   =============================== */

const containerBaseStyles = `
  inline-flex
  font-sans font-medium
  rounded-xl
  transition-all duration-300 ease-out
  relative
  gap-0
`;

const segmentBaseStyles = `
  relative
  inline-flex items-center justify-center
  font-medium
  transition-all duration-300 ease-out
  cursor-pointer
  whitespace-nowrap
  focus:outline-none
  focus-visible:z-20
  focus-visible:ring-2
  focus-visible:ring-brand
  focus-visible:ring-offset-2
  disabled:opacity-40 
  disabled:cursor-not-allowed
  overflow-hidden
`;

/* ===============================
   SIZES
   =============================== */

const sizeStyles: Record<SegmentedButtonSize, string> = {
  sm: 'h-8 px-4 text-ui-sm gap-2',
  md: 'h-10 px-5 text-ui-base gap-2',
  lg: 'h-12 px-7 text-body gap-2',
};

// Estilos para cuando solo hay iconos (sin label)
const iconOnlySizeStyles: Record<SegmentedButtonSize, string> = {
  sm: 'h-8 w-8 text-ui-sm',
  md: 'h-10 w-10 text-ui-base',
  lg: 'h-12 w-12 text-body',
};

const containerPadding: Record<SegmentedButtonSize, string> = {
  sm: 'p-1',
  md: 'p-1',
  lg: 'p-1.5',
};

const iconSizes: Record<SegmentedButtonSize, string> = {
  sm: 'w-3.5 h-3.5',
  md: 'w-[1.125rem] h-[1.125rem]',
  lg: 'w-5 h-5',
};

/* ===============================
   VARIANTS
   =============================== */

const variantStyles = {
  filled: {
    container: `
      bg-surface-2
      border border-strong
      backdrop-blur-sm
    `,
    segment: {
      default: `
        bg-transparent
        text-tertiary
        font-medium
        border border-transparent
        relative
        hover:text-secondary
        transition-all duration-300
      `,
      selected: `
        bg-surface-0
        text-primary
        font-semibold
        border border-strong
        shadow-sm
      `,
    },
  },
  outlined: {
    container: `
      bg-transparent
      border border-strong
    `,
    segment: {
      default: `
        bg-transparent
        text-secondary
        hover:bg-surface-hover/50
        hover:text-primary
        border-r border-strong
        last:border-r-0
      `,
      selected: `
        bg-brand
        text-inverse
        dark:text-primary
        border-brand
        font-semibold
        hover:bg-brand
      `,
    },
  },
  ghost: {
    container: `
      bg-transparent
      gap-0.5
    `,
    segment: {
      default: `
        bg-transparent
        text-secondary
        rounded-lg
        border border-transparent
        hover:bg-surface-hover
        hover:text-primary
      `,
      selected: `
        bg-primary-light
        text-brand
        rounded-lg
        font-semibold
        border border-brand/30
      `,
    },
  },
};

/* ===============================
   BORDER RADIUS
   =============================== */

const getBorderRadius = (
  index: number,
  total: number,
  variant: SegmentedButtonVariant,
  size: SegmentedButtonSize
): string => {
  const isFirst = index === 0;
  const isLast = index === total - 1;

  const radiusMap = {
    sm: { outer: 'rounded-[0.625rem]', inner: 'rounded-[0.5rem]' },
    md: { outer: 'rounded-[0.6875rem]', inner: 'rounded-[0.5625rem]' },
    lg: { outer: 'rounded-[0.8125rem]', inner: 'rounded-[0.6875rem]' },
  };

  const radius = radiusMap[size];

  if (variant === 'ghost') {
    return radius.inner;
  }

  if (variant === 'filled') {
    if (isFirst) return `${radius.inner} rounded-r-none`;
    if (isLast) return `${radius.inner} rounded-l-none`;
    return 'rounded-none';
  }

  if (variant === 'outlined') {
    if (isFirst) return `${radius.outer} rounded-r-none`;
    if (isLast) return `${radius.outer} rounded-l-none`;
    return 'rounded-none';
  }

  return '';
};

/* ===============================
   SEGMENTED BUTTON COMPONENT
   =============================== */

export const SegmentedButton = React.forwardRef<HTMLDivElement, SegmentedButtonProps>(
  (
    {
      options,
      value: controlledValue,
      defaultValue,
      onChange,
      size = 'md',
      variant = 'filled',
      fullWidth = false,
      className = '',
      disabled = false,
      'aria-label': ariaLabel,
    },
    ref
  ) => {
    // Determine if component is controlled or uncontrolled
    const isControlled = controlledValue !== undefined;
    const initialValue = controlledValue || defaultValue || options[0]?.value;

    const [internalValue, setInternalValue] = useState(initialValue);
    const selectedValue = isControlled ? controlledValue : internalValue;

    const handleClick = useCallback(
      (optionValue: string, optionDisabled?: boolean) => {
        if (disabled || optionDisabled) return;

        // Update internal state if uncontrolled
        if (!isControlled) {
          setInternalValue(optionValue);
        }

        // Call onChange callback
        onChange?.(optionValue);
      },
      [disabled, isControlled, onChange]
    );

    const containerClasses = [
      containerBaseStyles,
      variant !== 'ghost' && containerPadding[size],
      variantStyles[variant].container,
      fullWidth ? 'w-full' : '',
      className,
    ]
      .filter(Boolean)
      .join(' ')
      .replace(/\s+/g, ' ');

    const renderIcon = (iconElement: React.ReactNode) => {
      if (!iconElement) return null;
      return (
        <span
          className={`inline-flex items-center flex-shrink-0 transition-transform duration-300 ${iconSizes[size]}`}
          aria-hidden="true"
        >
          {iconElement}
        </span>
      );
    };

    return (
      <div
        ref={ref}
        className={containerClasses}
        role="radiogroup"
        aria-label={ariaLabel || 'Segmented button group'}
      >
        {options.map((option, index) => {
          const isSelected = selectedValue === option.value;
          const isDisabled = disabled || option.disabled;
          const hasLabel = option.label && option.label.trim() !== '';
          const hasTooltip = option.tooltip && option.tooltip.trim() !== '';

          const segmentClasses = [
            segmentBaseStyles,
            hasLabel ? sizeStyles[size] : iconOnlySizeStyles[size],
            isSelected
              ? variantStyles[variant].segment.selected
              : variantStyles[variant].segment.default,
            getBorderRadius(index, options.length, variant, size),
            fullWidth ? 'flex-1' : '',
          ]
            .filter(Boolean)
            .join(' ')
            .replace(/\s+/g, ' ');

          const buttonContent = (
            <button
              key={option.value}
              type="button"
              onClick={() => handleClick(option.value, option.disabled)}
              disabled={isDisabled}
              className={segmentClasses}
              aria-checked={isSelected}
              aria-disabled={isDisabled}
              role="radio"
              aria-label={hasLabel ? option.label : option.tooltip || option.value}
            >
              {option.icon && renderIcon(option.icon)}
              {hasLabel && (
                <span className="inline-flex items-center leading-[1.2] tracking-[-0.01em]">
                  {option.label}
                </span>
              )}
            </button>
          );

          // Si tiene tooltip y no tiene label (solo icono), envolver con Tooltip
          if (hasTooltip && !hasLabel) {
            return (
              <Tooltip key={option.value} text={option.tooltip!} position="top">
                {buttonContent}
              </Tooltip>
            );
          }

          return buttonContent;
        })}
      </div>
    );
  }
);

SegmentedButton.displayName = 'SegmentedButton';



